import React, {FormEvent, ReactElement, useState} from 'react';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import {IElementForm} from '../../../../incase-domain/elements/form';
import ElementTyped from './element.typed';
import {saveFormElement, sendFormToExternalService} from '../../../../service/formElement.service';
import {IFormElement} from '../../../../incase-domain/elements/base/formElementBase';
import {runFileIdSelector, runFilePresentationIdSelector} from '../../../../store/selectors/runfile.selectors';
import {DeviceType} from '../../../../incase-domain/common/DeviceType.enum';
import useDelayedLoader from '../../../../hooks/useDelayedLoader';

interface FormElementComponentProps {
    element: IElementForm;
    device?: DeviceType;
}
function FormElementComponent({element, device = DeviceType.DESKTOP}: FormElementComponentProps): ReactElement {
    const runFileId = useSelector(runFileIdSelector);
    const runFilePresentationId = useSelector(runFilePresentationIdSelector);

    const [isSubmitting, setIsSubmitting, isChanging] = useDelayedLoader(false);

    const [validated, setValidated] = useState<boolean[]>([]);
    const buttonDisabled = validated.some((val) => !val);

    const handleSetValidated = (index: number, val: boolean): void => {
        setValidated((prev) => {
            const newValidated = [...prev];
            newValidated[index] = val;
            return newValidated;
        });
    };

    const getChildWidth = (childElement: IFormElement): string => {
        return `${(childElement.formWidth || 1) * 100}%`;
    };

    const getChildPadding = (childElement: IFormElement): string => {
        return `${childElement.paddingBefore || 0}em`;
    };

    const getChildStyle = (childElement: IFormElement): {width: string; paddingLeft: string} => ({
        width: getChildWidth(childElement),
        paddingLeft: getChildPadding(childElement),
    });

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setIsSubmitting(true);

        const formData = new FormData(e.target as HTMLFormElement);

        const sendForm = async (): Promise<(IElementForm | unknown) & {failed?: boolean}> => {
            if (!element.submitUrl) {
                return saveFormElement(element.id, formData, runFileId, runFilePresentationId);
            }
            return sendFormToExternalService(element.submitUrl, element.method, element.contentType, formData);
        };

        const response = await sendForm();

        setIsSubmitting(false);
        if (response.failed) {
            toast.error('Something went wrong', {toastId: 'Something went wrong'});
        } else {
            toast.success('Form submitted', {toastId: 'Form submitted'});
        }
    };

    return (
        <div className="c-field is-lg c-formElement">
            <div className="e-label" style={getElementStyle(device, element)}>
                {element.title}
            </div>
            <form onSubmit={handleSubmit}>
                <div className="u-flex flex-wrap">
                    {element.fieldElements.map((childElement, index) => (
                        <div className="u-mt-2" style={getChildStyle(childElement)} key={`${element.id}-form-child-element-${index}`}>
                            <ElementTyped
                                device={device}
                                element={childElement}
                                isLoading={isChanging || isSubmitting}
                                setValidated={(val) => handleSetValidated(index, val)}
                                disabled={buttonDisabled}
                            />
                        </div>
                    ))}
                </div>
            </form>
        </div>
    );
}

function getElementStyle(device: DeviceType, element: IElementForm): React.CSSProperties {
    if (device === DeviceType.MOBILE) {
        return {fontFamily: element.fontFamily?.name};
    }

    return {fontSize: `${element.fontSize || 2}em`, fontFamily: element.fontFamily?.name};
}

export default FormElementComponent;
